import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

export const PrivacyPolicyModal = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || <Button variant="link" className="text-white/60 hover:text-white p-0 h-auto">Privacy Policy</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Privacy Policy</DialogTitle>
          <p className="text-sm text-muted-foreground">Autism Acceptance Resources Hub</p>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <p className="text-sm text-muted-foreground mb-4">Effective Date: January 2026</p>
            
            <p>At Autism Acceptance, we take your privacy seriously. This Privacy Policy explains how we collect, use, store, and protect your personal data when you use the Resources Hub or participate in our community forum.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">1. Data Controller</h3>
            <p>Autism Acceptance is the sole Data Controller for all information you provide through our website, including the Resources Hub and community forum.</p>
            <p><strong>Contact:</strong><br />Email: hello@autismacceptance.ky</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">2. Information We Collect</h3>
            <p>We may collect the following information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email, and contact information when registering for the Resources Hub</li>
              <li>Sensitive personal data you upload (e.g., health information, therapy notes, or other personal data related to autism)</li>
              <li>Content you post in the community forum</li>
              <li>Usage data (e.g., login activity, pages visited, resources downloaded)</li>
            </ul>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">3. How We Use Your Data</h3>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide access to the Resources Hub and community forum</li>
              <li>Facilitate communication regarding resources or account activity</li>
              <li>Maintain and improve our website and services</li>
              <li>Ensure compliance with legal obligations</li>
            </ul>
            <p className="mt-2">We do not share your personal or sensitive data with third parties outside of Autism Acceptance.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">4. Who Has Access</h3>
            <p>Only Autism Acceptance staff and authorised personnel have access to personal and sensitive data.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">5. User Rights under Cayman Islands DPL</h3>
            <p>Under the Data Protection Law (DPL), 2017, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Request access to the personal data we hold about you</li>
              <li>Request correction or deletion of your personal data</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at: hello@autismacceptance.ky</p>
            <p>We will respond to all requests promptly, in accordance with DPL requirements.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">6. Security</h3>
            <p>We implement appropriate technical and organisational measures to protect your data against unauthorized access, loss, or misuse.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">7. Community Forum Disclaimer</h3>
            <p>Any information you post in the community forum is visible to other registered users. Do not post personal information about others without consent.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">8. Changes to this Policy</h3>
            <p>We may update this policy from time to time. Any changes will be posted on this page with an updated effective date.</p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export const TermsOfUseModal = ({ trigger }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || <Button variant="link" className="text-white/60 hover:text-white p-0 h-auto">Terms of Use</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Terms of Use</DialogTitle>
          <p className="text-sm text-muted-foreground">Autism Acceptance Resources Hub</p>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <p className="text-sm text-muted-foreground mb-4">Effective Date: January 2026</p>
            
            <p>By using the Autism Acceptance Resources Hub or participating in the community forum, you agree to the following terms:</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">1. Acceptance of Terms</h3>
            <p>By accessing our website, you agree to abide by these Terms of Use and our Privacy Policy.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">2. Eligibility</h3>
            <p>You must be at least 18 years old or have the consent of a parent/guardian to register and use the Resources Hub or community forum.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">3. Use of the Resources Hub</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Resources are provided for informational purposes only and do not replace professional advice.</li>
              <li>You may not distribute, copy, or use materials for commercial purposes without our consent.</li>
            </ul>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">4. Community Forum Rules</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Be respectful and considerate of others.</li>
              <li>Do not post illegal, offensive, or abusive content.</li>
              <li>Do not post sensitive personal data about others without their consent.</li>
            </ul>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">5. Intellectual Property</h3>
            <p>All content on the Resources Hub, including text, images, and resources, is the property of Autism Acceptance or its licensors. You may not use it without permission.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">6. Disclaimer</h3>
            <p>We provide the Resources Hub and forum "as is." Autism Acceptance is not liable for any loss, damage, or misuse of information posted or accessed through the website.</p>
            
            <div className="bg-muted/50 p-4 rounded-lg mt-4">
              <p className="font-semibold text-sm">Resource Disclaimer:</p>
              <p className="text-sm text-muted-foreground">The resources, articles, and information provided on this platform are for educational and informational purposes only. They do not represent the official views, opinions, or endorsements of Autism Acceptance. The content has been sourced from various materials and should not be considered as professional medical, legal, or therapeutic advice. Always consult with qualified professionals for specific guidance related to autism support and services.</p>
            </div>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">7. Modification & Termination</h3>
            <p>We may update these Terms of Use at any time. Continued use constitutes acceptance. We may also suspend or terminate access for users who violate these terms.</p>
            
            <h3 className="text-lg font-semibold mt-6 mb-3">8. Governing Law</h3>
            <p>These terms are governed by the laws of the Cayman Islands.</p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
